import React from 'react';
import { Autocomplete, Chip, styled, TextField, Tooltip } from '@mui/material';
import {
    AutocompleteRenderInputParams,
    AutocompleteProps,
} from '@mui/material/Autocomplete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';

const StyAutocomplete = styled(Autocomplete, {
    name: 'StyAutocomplete',
})(() => ({
    padding: '0.3rem',
    '& .MuiInput-root .MuiInput-input': {
        padding: 'unset',
    },
}));

const StyMultiValue = styled(Chip, {
    name: 'StyMultiValue',
})(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    lineHeight: '1.5em',
    height: 'auto',
    fontSize: 14,
    backgroundColor: theme.palette.primary.background,
    borderRadius: '0.2rem',
    '.MuiChip-deleteIcon': {
        color: '#94a0c8',
    },
}));

const StyInputComponent = styled(TextField, {
    name: 'StyInputComponent',
    shouldForwardProp: (prop) =>
        !['isMultiple', 'valueIsEmpty'].includes(prop as string),
})<{
    isMultiple: boolean;
    valueIsEmpty: boolean;
}>(({ theme, isMultiple, valueIsEmpty }) => {
    const commonInputStyles = {
        fontSize: 16,
        backgroundColor:
            valueIsEmpty || isMultiple
                ? undefined
                : theme.palette.primary.background,
        borderRadius: '0.2rem',
        padding: isMultiple ? undefined : '0 0.3rem 0rem 0.3rem ',
    };

    return {
        display: 'flex',
        height: 'unset',
        '& .MuiInputBase-input': {
            ...commonInputStyles,
        },
    };
});

type GenericAutocompleteProps<T> = {
    options: T[];
    value: T | T[] | null;
    onChange: (newValue: T | T[] | null) => void;
    getOptionLabel?: (option: T) => string;
    placeholder?: string;
    inputWidth?: string;
    labelField?: string;
} & Omit<
    AutocompleteProps<T, boolean, boolean, boolean>,
    | 'options'
    | 'value'
    | 'onChange'
    | 'getOptionLabel'
    | 'renderInput'
    | 'renderTags'
    | 'multiple'
>;

export default function BudgetAutocomplete<T>({
    options,
    value,
    onChange,
    getOptionLabel = (option) => String(option),
    placeholder = '',
    inputWidth = undefined,
    labelField = '',
    ...autocompleteProps
}: GenericAutocompleteProps<T>): React.ReactElement {
    const { t } = useTranslation();
    // Determine if the Autocomplete should be multiple based on the value type
    const isMultiple = Array.isArray(value);
    const inputMinWidth = Math.max(10, placeholder.length * 1.5);

    return (
        <StyAutocomplete
            multiple={isMultiple}
            options={options}
            value={value}
            onChange={(_event, newValue) =>
                onChange(newValue as T | T[] | null)
            }
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isEqual}
            popupIcon={<KeyboardArrowDownIcon />}
            renderInput={(params: AutocompleteRenderInputParams) => (
                <Tooltip
                    title={isMultiple || !value ? '' : getOptionLabel(value)}
                >
                    <StyInputComponent
                        {...params}
                        variant="standard"
                        placeholder={placeholder}
                        isMultiple={isMultiple}
                        valueIsEmpty={value == null}
                        label={labelField}
                        sx={{
                            '& .MuiInputBase-root': {
                                paddingBottom: isMultiple
                                    ? undefined
                                    : '0.3rem',
                                width: inputWidth,
                                minWidth: `${inputMinWidth}ch`,
                            },
                        }}
                    />
                </Tooltip>
            )}
            renderTags={(tagValues, _getTagProps) =>
                tagValues.map((option) => {
                    const label = `${option}`;
                    return (
                        <Tooltip key={label} title={label}>
                            <StyMultiValue label={label} />
                        </Tooltip>
                    );
                })
            }
            noOptionsText={t('common.noResult')}
            {...autocompleteProps}
        />
    );
}
